import { useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CharacterCounter } from '@/components/ui/character-counter';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { gateReasonsOf, gateReasonText, isUnansweredRequest } from '@/lib/template-admin';
import { TEMPLATE_LIMITS } from '@/lib/template-labels';
import { toastIfSilent } from '@/lib/template-toast';
import { formTemplatesService, type AdminTemplateDetail, type PublishGateReason } from '@/services/formTemplates';
import toastService from '@/services/toast/service';

export const PUBLISH_UNANSWERED_TEXT = 'No answer from the server, reloading to check the publish';

interface PublishVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: AdminTemplateDetail;
  /** Reloads the page and toasts; called after the dialog closed. */
  onPublished: (versionNo: number) => Promise<void>;
  /** Reloads the page; called when the publish request got no answer, so the page shows whether it went through. */
  onUnanswered: () => Promise<unknown>;
  onOpenBuilder: () => Promise<void>;
}

export function PublishVersionDialog({
  open,
  onOpenChange,
  detail,
  onPublished,
  onUnanswered,
  onOpenBuilder,
}: PublishVersionDialogProps) {
  const [changeNote, setChangeNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [reasons, setReasons] = useState<PublishGateReason[]>([]);

  const next = (detail.versions[0]?.versionNo ?? 0) + 1;

  useEffect(() => {
    if (open) {
      setChangeNote('');
      setReasons([]);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (publishing) return;
    setReasons([]);
    setPublishing(true);
    try {
      const { version } = await formTemplatesService.publish(detail.publicId, changeNote.trim() || null);
      onOpenChange(false);
      await onPublished(version.versionNo);
    } catch (err) {
      const gate = gateReasonsOf(err);
      if (gate === null && isUnansweredRequest(err)) {
        // The version may have been committed: show what the server has, don't offer a second click.
        toastService.error(PUBLISH_UNANSWERED_TEXT);
        onOpenChange(false);
        await onUnanswered();
      } else {
        // A server message was already toasted by the api client; the dialog stays open.
        toastIfSilent(err, "Couldn't publish the version");
        setReasons(gate ?? []);
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !publishing && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{`Publish version ${next}`}</DialogTitle>
            <DialogDescription>
              {`Freezes the studio form and its images as version ${next}. Customers who use this template from now on get it; copies they already made don't change.`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="publish-change-note">Change note (optional)</Label>
                <CharacterCounter current={changeNote.length} max={TEMPLATE_LIMITS.changeNote} />
              </div>
              <Input
                id="publish-change-note"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                maxLength={TEMPLATE_LIMITS.changeNote}
                placeholder='What changed, e.g. "Shorter contact step"'
                disabled={publishing}
              />
            </div>
            {reasons.length > 0 && (
              <div className="flex flex-col items-start gap-2">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>Fix these in the builder, then publish again:</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {reasons.map((reason, i) => (
                        <li key={i}>{gateReasonText(reason)}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
                <Button type="button" variant="outline" size="sm" onClick={() => void onOpenBuilder()}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in builder
                </Button>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={publishing}>
              Cancel
            </Button>
            <Button type="submit" disabled={publishing}>
              {publishing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                `Publish version ${next}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
